import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { type HeldOrgFunctionRef } from '@bliks-io/contracts';
import { useMyOrgFunctions } from '@/api/queries/useOrgFunctions';
import { useAuthStore } from '@/stores/auth-store';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { orgFunctionDetailRoute } from '@/lib/orgFunctionRoutes';
import { ContentHeader } from '@/components/content-header';
import { WorkEnvironmentTabsNav } from '@/components/work-environment/WorkEnvironmentTabsNav';
import {
  Page,
  PageHeader,
  PageContent,
  DataTable,
  OrgFunctionKindBadge,
  Badge,
  createColumnHeader,
  appendDeletedSuffix,
  DELETED_ENTITY_CLASS,
  cn,
  type DataTableColumnDef
} from '@bliks-io/ui';

/**
 * Meine Funktionen — every OrgFunction the current user holds, Stelle first.
 * This is a lookup surface, so soft-deleted OrgFunctions still resolve and are
 * marked with a tombstone suffix.
 */
export function MyOrgFunctionsPage() {
  const { t } = useTranslation();
  useDocumentTitle('pageTitles.myOrgFunctions');
  const slug = useAuthStore().slug();

  const { data: orgFunctions, isLoading } = useMyOrgFunctions();

  const columns = useMemo<DataTableColumnDef<HeldOrgFunctionRef>[]>(
    () => [
      {
        accessorKey: 'name',
        meta: { label: t('common.Name'), filterType: 'text' },
        header: createColumnHeader(t('common.Name')),
        cell: ({ row }) => {
          const isDeleted = typeof row.original.deletedAt === 'number';
          const name = appendDeletedSuffix(row.original.name, isDeleted, t);
          return (
            <span className={cn('flex items-center gap-2', isDeleted && DELETED_ENTITY_CLASS)}>
              <Link
                to={orgFunctionDetailRoute(row.original.kind)}
                params={{ slug, orgFunctionId: row.original._id }}
                className="font-medium hover:underline"
              >
                {name}
              </Link>
              {row.original.via === 'main' && (
                <Badge variant="outline" className="shrink-0">
                  {t('organization.members.mainRole')}
                </Badge>
              )}
            </span>
          );
        },
        enableColumnFilter: true
      },
      {
        accessorKey: 'kind',
        meta: { label: t('organization.orgFunctions.kind.label') },
        header: createColumnHeader(t('organization.orgFunctions.kind.label')),
        cell: ({ row }) => (
          <OrgFunctionKindBadge
            kind={row.original.kind}
            label={t(`organization.orgFunctions.kind.${row.original.kind}` as const)}
          />
        )
      }
    ],
    [t, slug]
  );

  return (
    <Page>
      <PageHeader>
        <WorkEnvironmentTabsNav />
      </PageHeader>
      <PageContent>
        <ContentHeader
          icon={ENTITY_ICONS.orgFunction}
          title={t('organization.myOrgFunctions.title')}
          description={t('organization.myOrgFunctions.description')}
        />

        <div className="mt-6">
          <DataTable
            columns={columns}
            data={orgFunctions ?? []}
            isLoading={isLoading}
            emptyMessage={t('organization.myOrgFunctions.empty')}
            enableColumnFilters
            enableUrlState
            tableId="my-org-functions"
          />
        </div>
      </PageContent>
    </Page>
  );
}
